import {forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode} from 'react';
import {cn} from '../lib/utils';
import {Stack} from '../Stack';
import {Typography} from '../Typography';
import {Success1Icon} from '../icons';

export interface CheckboxProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'className'> {
	label?: ReactNode;
	error?: ReactNode;
	hint?: ReactNode;
	required?: boolean;
	indeterminate?: boolean;
	className?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({
	label,
	error,
	hint,
	required = false,
	indeterminate = false,
	id,
	className,
	...props
}, ref) => {
	const generatedId = useId();
	const inputId = id ?? generatedId;
	const messageId = `${inputId}-message`;
	const message = error ?? hint;

	const setRefs = (el: HTMLInputElement | null) => {
		if (el) el.indeterminate = indeterminate;
		if (typeof ref === 'function') ref(el);
		else if (ref) (ref as {current: HTMLInputElement | null}).current = el;
	};

	return (
		<div data-slot='checkbox-field' className={cn(className, 'flex flex-col gap-1')}>
			<Stack as='label' htmlFor={inputId} className={rowStyles}>
				<span className='relative inline-flex'>
					<input
						ref={setRefs}
						id={inputId}
						type='checkbox'
						data-slot='checkbox'
						aria-required={required || undefined}
						aria-invalid={error ? true : undefined}
						aria-describedby={message ? messageId : undefined}
						className={cn(boxStyles, error && 'border-tone-red')}
						{...props}/>
					{indeterminate
						? <span aria-hidden className={dashStyles}/>
						: <Success1Icon aria-hidden className={checkStyles}/>}
				</span>
				{label &&
					<Stack as='span' gap={0.5}>
						<Typography variant='small1' color='textSecondary'>{label}</Typography>
						{required &&
							<Typography variant='small1' color='critical' aria-hidden>*</Typography>}
					</Stack>}
			</Stack>
			{message &&
				<Typography id={messageId} variant='small2' color={error ? 'critical' : 'textMuted'}>
					{message}
				</Typography>}
		</div>
	);
});

Checkbox.displayName = 'Checkbox';

const rowStyles = 'w-fit cursor-pointer has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50';

const boxStyles =
	'peer size-4 shrink-0 cursor-pointer appearance-none rounded border border-foreground-400 bg-transparent ' +
	'transition-colors checked:border-brand-main checked:bg-brand-main ' +
	'indeterminate:border-brand-main indeterminate:bg-brand-main ' +
	'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-main-light ' +
	'disabled:cursor-not-allowed';

const checkStyles = 'pointer-events-none absolute inset-0 m-auto hidden size-3 text-foreground-white peer-checked:block';

const dashStyles = 'pointer-events-none absolute inset-x-[3px] top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-foreground-white';
